import React, { useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ReportProblemOutlined,
  DoneAll,
} from "@mui/icons-material";
import { Base_Uri } from "../../Constant/BaseUri";

const getPartName = (part) =>
  part?.productData?.name ||
  part?.productId?.productName ||
  part?.productId?.name ||
  "Part";

const getPartSku = (part) =>
  part?.productData?.sku ||
  part?.productId?.sku ||
  "";

const getToken = () => {
  const stored = sessionStorage.getItem("userData");
  return stored ? JSON.parse(stored) : null;
};

const FaultyManager = ({ job, onChanged }) => {
  const userData = useSelector(
    (state) => state.loginReducer.updatedState,
  );

  const isAdmin =
    String(userData?.role || "").toLowerCase() === "admin";

  const parts = useMemo(
    () => (Array.isArray(job?.products) ? job.products : []),
    [job?.products],
  );

  const faultyItems = useMemo(
    () =>
      Array.isArray(job?.faultyItems)
        ? job.faultyItems
        : [],
    [job?.faultyItems],
  );

  const [open, setOpen] = useState(false);
  const [selectedPartId, setSelectedPartId] = useState("");
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [collectingId, setCollectingId] = useState("");
  const [error, setError] = useState("");

  const selectedPart = parts.find(
    (part) => String(part?._id) === String(selectedPartId),
  );

  const openDialog = () => {
    setError("");
    setReason("");
    setQty(1);
    setSelectedPartId(parts?.[0]?._id || "");
    setOpen(true);
  };

  const closeDialog = () => {
    if (saving || collectingId) return;
    setOpen(false);
  };

  const handleSaveFaulty = async () => {
    try {
      setError("");

      if (!selectedPartId) {
        setError("Please select the faulty part.");
        return;
      }

      const safeQty = Number(qty);

      if (!Number.isInteger(safeQty) || safeQty < 1) {
        setError("Faulty quantity must be at least 1.");
        return;
      }

      if (safeQty > Number(selectedPart?.qty || 0)) {
        setError(
          `Faulty quantity cannot exceed ${selectedPart?.qty || 0}.`,
        );
        return;
      }

      if (!String(reason || "").trim()) {
        setError("Please enter the faulty reason.");
        return;
      }

      setSaving(true);

      const token = getToken();

      await axios.post(
        `${Base_Uri}repair-jobs/${job?._id}/faulty`,
        {
          repairProductId: selectedPartId,
          qty: safeQty,
          reason: String(reason).trim(),
          // Same SKU replacement. Backend also supports replacementProductId
          // later if you want to allow a different replacement SKU.
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      setReason("");
      setQty(1);

      if (onChanged) {
        await onChanged();
      }
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Unable to record faulty item.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleAdminCollect = async (faultyId) => {
    try {
      setError("");
      setCollectingId(String(faultyId));

      const token = getToken();

      await axios.patch(
        `${Base_Uri}repair-jobs/${job?._id}/faulty/${faultyId}/collect`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (onChanged) {
        await onChanged();
      }
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Unable to collect faulty item.",
      );
    } finally {
      setCollectingId("");
    }
  };

  const hasParts = parts.length > 0;
  const pendingFaultyCount = faultyItems.filter(
    (item) =>
      item?.adminCollection?.status !== "Collected",
  ).length;

  return (
    <>
      <Tooltip
        title={
          hasParts
            ? "Faulty / Replace Part"
            : "No parts attached to this job"
        }
      >
        <span>
          <IconButton
            color="warning"
            onClick={openDialog}
            disabled={!hasParts}
            size="small"
          >
            <ReportProblemOutlined fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      {pendingFaultyCount > 0 && (
        <Chip
          size="small"
          color="warning"
          label={pendingFaultyCount}
          sx={{ ml: 0.5 }}
        />
      )}

      <Dialog
        open={open}
        onClose={closeDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Faulty / Replacement - {job?.jobNo || "Repair Job"}
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Job Status: <strong>{job?.status || "-"}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Customer: {job?.customerData?.customer_name || "-"}
              </Typography>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}

            <FormControl fullWidth size="small">
              <InputLabel>Faulty Part</InputLabel>
              <Select
                label="Faulty Part"
                value={selectedPartId}
                onChange={(event) => {
                  setSelectedPartId(event.target.value);
                  setQty(1);
                }}
              >
                {parts.map((part) => (
                  <MenuItem key={part?._id} value={part?._id}>
                    {getPartName(part)}
                    {getPartSku(part)
                      ? ` - ${getPartSku(part)}`
                      : ""}
                    {` (Qty: ${part?.qty || 0})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              size="small"
              type="number"
              label="Faulty Quantity"
              value={qty}
              inputProps={{
                min: 1,
                max: Number(selectedPart?.qty || 1),
              }}
              onChange={(event) => setQty(event.target.value)}
            />

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Faulty Reason"
              placeholder="Example: Replacement screen has no touch / part is not working"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />

            <Alert severity="info">
              A fresh unit of the same product will be issued as the replacement.
              The faulty unit will not be returned to saleable stock.
            </Alert>

            <Box>
              <Button
                variant="contained"
                color="warning"
                onClick={handleSaveFaulty}
                disabled={saving || !selectedPartId}
              >
                {saving ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  "Add Faulty & Replace"
                )}
              </Button>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                Faulty History
              </Typography>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Faulty Part</TableCell>
                      <TableCell>Qty</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Replacement</TableCell>
                      <TableCell>Reported</TableCell>
                      <TableCell>Admin Collection</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {faultyItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No faulty items recorded.
                        </TableCell>
                      </TableRow>
                    ) : (
                      faultyItems.map((item) => (
                        <TableRow key={item?._id}>
                          <TableCell>
                            {item?.productData?.name || "-"}
                          </TableCell>
                          <TableCell>{item?.qty || 0}</TableCell>
                          <TableCell sx={{ maxWidth: 240 }}>
                            {item?.reason || "-"}
                          </TableCell>
                          <TableCell>
                            {item?.replacement?.productData?.name || "-"}
                          </TableCell>
                          <TableCell>
                            {item?.reportedAt
                              ? new Date(item.reportedAt).toLocaleString("en-GB")
                              : "-"}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip
                                size="small"
                                color={
                                  item?.adminCollection?.status === "Collected"
                                    ? "success"
                                    : "warning"
                                }
                                label={
                                  item?.adminCollection?.status || "Pending"
                                }
                              />

                              {isAdmin &&
                                item?.adminCollection?.status !== "Collected" && (
                                  <Tooltip title="Admin collect faulty item">
                                    <span>
                                      <IconButton
                                        size="small"
                                        color="success"
                                        disabled={collectingId === String(item?._id)}
                                        onClick={() =>
                                          handleAdminCollect(item?._id)
                                        }
                                      >
                                        {collectingId === String(item?._id) ? (
                                          <CircularProgress size={18} />
                                        ) : (
                                          <DoneAll fontSize="small" />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeDialog} disabled={saving || !!collectingId}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FaultyManager;
